// MAR-130 / MAR-167: dismissible banner promoting the Capsule Chic Hub app on
// the App Store and Google Play.
//
// LIVE. Gated on APP_BANNER_ENABLED, which is true unless
// VITE_APP_BANNER_ENABLED is set to exactly "false" at build time. It shipped
// dark in MAR-130 while version 1.0.0 sat in WAITING_FOR_REVIEW and the App
// Store URL 404d; the listing is live now, so MAR-167 inverted the default
// and the environment variable became a kill switch. See constants.ts.
//
// NOT ON THE PAGE GOOGLE RANKS (Oct 2026). Style Hub articles are the
// site's organic landing pages, and a promo covering the bottom of one is
// what Google's intrusive interstitial rules penalise. Three rules, all in
// lib/app-banner:
//   - Never for a crawler (isCrawler).
//   - Not on the first view of a page load that lands on an article from
//     outside the site (isExternalArticleLanding). It appears once the
//     reader moves to another page in the SPA, and on any later page load,
//     whose referrer is then our own origin.
//   - On an article it is never fixed: it renders as an ordinary row at the
//     very end of the document, after the footer, so it can never cover the
//     text. Home, shop and the rest keep the fixed bar.
//
// NO LAYOUT SHIFT, by construction:
//   - `position: fixed` puts it outside document flow, so it can never push
//     content around. Nothing above it reflows when it appears. The in-flow
//     article row is the last thing in the document, so nothing sits below
//     it to be pushed.
//   - It renders only after mount, so the prerendered HTML that
//     scripts/prerender.js writes never contains it. Googlebot's first paint
//     and scripts/verify-build-output.mjs are both untouched.
//   - Every <img> (badge, QR code, app icon) carries explicit width and
//     height attributes, so the banner's own internal box is reserved before
//     the image decodes.
//
// IT IS NOT THE ONLY THING AT THE BOTTOM OF THE VIEWPORT. Before adding any
// other bottom-fixed element, read isBannerRouteSuppressed in lib/app-banner
// and the --app-banner-height contract below. Getting this wrong hides a
// conversion CTA behind a promo.
//
// THE BACKGROUND MUST STAY FULLY OPAQUE (MAR-168). It shipped as
// `bg-warm-white/95 backdrop-blur-sm`, and the hero image read straight
// through it on a real iPhone. A translucent banner has no contrast ratio at
// all: the number changes with whatever is scrolled underneath it, so no
// static check describes what a reader sees. Every colour here now comes from
// a brand token in client/src/index.css, and those tokens flip under `.dark`,
// which is why there are no `dark:` overrides left to keep in step. The
// ratios are computed from the real token values and asserted in
// __tests__/AppDownloadBanner.contrast.test.tsx. Reintroducing an alpha
// modifier on the background, or dropping the text back to
// `text-muted-foreground` (4.61:1, and lower over an image), fails that suite.
//
// PHONES GET ONE ROW: COPY, THEIR STORE'S BADGE, CLOSE. About 76px tall. The
// badge with its clear space costs 140px (App Store) or 155px (Google Play) of
// a 360px screen, and it cannot shrink: Apple's web minimum is 40px tall and
// both stores require the clear space. An earlier layout put the app icon and
// a long line of copy beside it, which left the copy a column about 80px wide;
// on a Samsung the heading wrapped to three lines, the body to six, and the
// banner took a third of the screen. #1075 fixed that by swapping the badge
// for a text "Get" pill. This layout keeps the badge and pays for it instead:
// no app icon (the badge carries the visual weight), and copy short enough to
// sit on two single lines in the ~135px left at 360px wide. The badge's own
// alt text is the link's accessible name, as on desktop, so what a screen
// reader hears matches the words printed on the badge.
//
// APPLE'S AND GOOGLE'S BADGE RULES ARE BINDING, NOT STYLISTIC. Breaking them
// is grounds for takedown. If you touch a badge below, on either layout, keep
// all of these true:
//   - Render /badges/download-on-the-app-store.svg and
//     /badges/get-it-on-google-play.svg exactly as supplied. The Play badge is
//     Google's own English web SVG from the Partner Marketing Hub, copied
//     byte for byte. Never redraw either, recolour it, rotate it, crop it, or
//     put effects on it. That rules out CSS filter, opacity, mix-blend-mode,
//     border-radius clipping and transform on the <img> itself.
//   - Height is 40px for both. Apple's web minimum is 40 and Google's is 28,
//     and Google also requires the Play badge to be the same size as or
//     larger than any other store badge beside it, so they share one height.
//   - Clear space of at least 1/4 the badge height must surround each one
//     (both Apple and Google ask for exactly that). The height and the clear
//     space are inline styles derived from the constants below, and both are
//     asserted in the test suite, so no utility class can quietly breach them.
//     The clear space is the link's own padding, so nothing else can sit in
//     it. It may overlap empty space around it, which is why the phone row
//     puts no gap either side of the badge link.
//   - Each badge IS the call to action for its store. Do not add a second
//     "Download" button. The close control is not a competing CTA, and
//     neither is a desktop QR code: it is a plain <img>, not a link and not a
//     button, so it cannot be clicked and cannot compete. Each QR code sits
//     outside its badge link's clear-space padding with a further gap, so it
//     never encroaches on the 1/4-height margin.
//
// THE DESKTOP QR CODES ARE PRE-RENDERED ARTWORK (MAR-181), committed at
// client/public/qr/app-store.svg and client/public/qr/google-play.svg, one
// per store. Not a runtime library and not a third-party
// image service: a service would put a tracking surface on every desktop page
// load and take the banner down with it, and a client-side encoder would be
// kilobytes of JavaScript shipped to every visitor to draw one image that
// never changes. A static SVG costs zero bundle JavaScript.
//
// The cost of pre-rendering is that the artwork can drift from APP_STORE_URL
// or PLAY_STORE_URL, which is the one defect a visual check cannot catch: a QR
// code that renders beautifully and encodes the wrong string looks perfect. So
// __tests__/AppDownloadBanner.qr.test.tsx DECODES each committed SVG and
// asserts the payload equals its store URL, from the real constants module.
// If you change either URL, regenerate its artwork; that test is what tells
// you so. Both were cut with the `qrcode` npm package (1.5.4) at error
// correction level M, one <rect> per horizontal run of dark modules, inside a
// four-module white quiet zone. The Play URL was encoded as a single byte
// segment.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import {
  detectPlatform,
  dismissBanner,
  isBannerDismissed,
  isArticleRoute,
  isBannerRouteSuppressed,
  isCrawler,
  isExternalArticleLanding,
  type AppPlatform,
} from "@/lib/app-banner";
import {
  APP_BANNER_ENABLED,
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

/** Both badges render at this height. See the rules above. */
export const BADGE_HEIGHT = 40;
/** Apple's and Google's required clear space: a quarter of the badge height. */
export const BADGE_CLEAR_SPACE = BADGE_HEIGHT / 4;

/**
 * Rendered size of each QR code, in CSS pixels, square.
 *
 * The Play URL is 68 bytes, which needs a version 5 symbol at level M: 37
 * modules inside a 4-module quiet zone, so 45 units across. At 96px that is
 * about 2.1px per module, the densest of the two codes and still above the
 * 2px floor where codes stop scanning off a screen (the App Store code, at 37
 * units, gets about 2.6px). Both share one size so the pair reads as a set.
 * Set as width and height attributes so the box is reserved before the SVG
 * decodes.
 */
export const QR_SIZE = 96;

export type StoreId = "app_store" | "google_play";

export interface StoreBadge {
  id: StoreId;
  href: string;
  badgeSrc: string;
  badgeAlt: string;
  /**
   * The badge's intrinsic width at BADGE_HEIGHT, rounded up so the reserved
   * box is never narrower than the rendered art.
   */
  badgeWidth: number;
  /** Pre-rendered QR code encoding `href`. See the note above. */
  qrSrc: string;
  qrAlt: string;
  /** GA4 event fired when the badge is clicked, on any layout. */
  clickEvent: string;
}

export const STORES: Record<StoreId, StoreBadge> = {
  app_store: {
    id: "app_store",
    href: APP_STORE_URL,
    badgeSrc: "/badges/download-on-the-app-store.svg",
    badgeAlt: "Download Capsule Chic Hub on the App Store",
    // Apple's badge is intrinsically 119.66407 x 40.
    badgeWidth: 120,
    qrSrc: "/qr/app-store.svg",
    qrAlt: "QR code that opens the Capsule Chic Hub app on the App Store",
    // The name predates Google Play and is kept so the series is unbroken.
    clickEvent: "app_banner_app_store_click",
  },
  google_play: {
    id: "google_play",
    href: PLAY_STORE_URL,
    badgeSrc: "/badges/get-it-on-google-play.svg",
    badgeAlt: "Get Capsule Chic Hub on Google Play",
    // Google's viewBox is 238.96 x 70.87, so 134.87 wide at 40 tall.
    badgeWidth: 135,
    qrSrc: "/qr/google-play.svg",
    qrAlt: "QR code that opens the Capsule Chic Hub app on Google Play",
    clickEvent: "app_banner_play_store_click",
  },
};

/**
 * Which stores each platform is offered. A visitor only ever sees a badge
 * they can act on: an iPhone cannot install from Google Play and an Android
 * phone cannot install from the App Store. Desktop gets both, because the
 * reader's phone could be either.
 */
export const STORES_FOR_PLATFORM: Record<AppPlatform, StoreId[]> = {
  ios: ["app_store"],
  android: ["google_play"],
  other: ["app_store", "google_play"],
};

/**
 * Custom property other bottom-fixed elements read so they can sit above the
 * banner instead of under it. Set on <html> while the banner is mounted and
 * removed when it goes, so consumers can default it to 0px and forget about
 * it. Currently read by ScrollToTopButton.
 */
const HEIGHT_VAR = "--app-banner-height";

/**
 * Phone copy. The heading is the app's name, as a native app banner shows it,
 * and each line has to fit on one line in the ~135px the badge and the close
 * control leave at 360px wide. Both platforms share it: the only thing that
 * differs on a phone is which store's badge sits beside it.
 */
export const PHONE_HEADING = "Capsule Chic Hub";
export const PHONE_COPY = "My picks, in the app";

/**
 * Desktop copy. Both badges, each with its QR code, because a reader on a
 * laptop can neither install the app nor retype a store URL.
 *
 * This line changed in MAR-181 and must keep describing what is actually
 * beside it. "Open this page on your phone to download it" was the only
 * instruction available when desktop got text alone; it is wrong now that
 * there is a code to scan, and copy that describes a missing affordance reads
 * as a broken page.
 */
const DESKTOP_COPY =
  "The Capsule Chic Hub app is on iPhone and Android. Point your phone camera at the code for your phone to install it.";

export default function AppDownloadBanner() {
  const [location] = useLocation();

  // Start hidden and decide on the client. Two reasons: the prerendered HTML
  // must not contain the banner (see the CLS note above), and platform
  // detection needs a real navigator.
  const [platform, setPlatform] = useState<AppPlatform | null>(null);
  // The route this page load started on. The banner mounts once, outside the
  // Router, so its first render sees the landing URL.
  const [landingPath] = useState(location);
  const [heldOnLanding, setHeldOnLanding] = useState(false);

  const bannerRef = useRef<HTMLDivElement | null>(null);
  // Whatever had focus before the visitor tabbed into the banner. Restored on
  // dismiss so keyboard users are not dropped back to the top of the document.
  const focusBeforeBanner = useRef<Element | null>(null);

  useEffect(() => {
    if (!APP_BANNER_ENABLED) return;
    if (isBannerDismissed()) return;
    if (typeof navigator !== "undefined" && isCrawler(navigator.userAgent)) return;
    setHeldOnLanding(
      isExternalArticleLanding(landingPath, document.referrer, window.location.origin),
    );
    setPlatform(detectPlatform());
  }, [landingPath]);

  // The hold ends for good at the first move to another page, so coming back
  // to the landing article later counts as a later view and shows the row.
  useEffect(() => {
    if (location !== landingPath) setHeldOnLanding(false);
  }, [location, landingPath]);

  const suppressed = isBannerRouteSuppressed(location) || heldOnLanding;
  const visible = platform !== null && !suppressed;
  // In the flow, at the end of the document, on articles. See the top note.
  const inline = isArticleRoute(location);

  // Exactly one impression per mount, deduped by ref.
  //
  // The effect's deps are [visible, platform], and `visible` flips off and on
  // again whenever the visitor navigates through a suppressed route. Without
  // the ref, /shop then /body-shape-quiz then /shop logs two impressions for
  // one person. This event is the DENOMINATOR for app_banner_app_store_click,
  // so route churn would inflate it and deflate the measured click-through.
  // The banner mounts once for the life of the page (it sits outside the
  // Router in App.tsx), so once per mount means once per page load.
  const impressionLogged = useRef(false);
  useEffect(() => {
    if (!visible || platform === null) return;
    if (impressionLogged.current) return;
    impressionLogged.current = true;
    trackEvent("app_banner_impression", { platform });
  }, [visible, platform]);

  // Publish the height so other bottom-fixed elements can clear it. Measured
  // rather than hard-coded because the copy wraps to two lines on narrow
  // screens. The in-flow article row covers nothing, so it publishes nothing.
  useEffect(() => {
    const node = bannerRef.current;
    if (!visible || inline || !node) return;

    const publish = () => {
      document.documentElement.style.setProperty(
        HEIGHT_VAR,
        `${node.offsetHeight}px`,
      );
    };
    publish();

    // ResizeObserver is absent in older jsdom and some in-app browsers; the
    // one-shot measurement above is the fallback.
    const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver })
      .ResizeObserver;
    const observer = RO ? new RO(publish) : null;
    observer?.observe(node);

    return () => {
      observer?.disconnect();
      document.documentElement.style.removeProperty(HEIGHT_VAR);
    };
  }, [visible, inline]);

  const handleDismiss = useCallback(() => {
    if (platform !== null) {
      trackEvent("app_banner_dismissed", { platform });
    }
    dismissBanner();
    setPlatform(null);

    // The close button is about to unmount. Put focus somewhere deliberate
    // rather than letting it fall to <body>, which restarts tab order.
    const previous = focusBeforeBanner.current;
    if (previous instanceof HTMLElement && previous.isConnected) {
      previous.focus();
    }
  }, [platform]);

  // Escape is scoped to the banner subtree, NOT bound on window. A global
  // listener would mean closing any Radix dialog with Escape also silently
  // dismissed the banner forever. Keydown bubbles here from the close button
  // and the store links, which are the only focusable things inside.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      handleDismiss();
    },
    [handleDismiss],
  );

  if (!visible || platform === null) return null;

  // The store's official badge inside its link. One rendering for both
  // layouts, so the rules above are enforced in one place.
  const badgeLink = (store: StoreBadge) => (
    <a
      key={store.id}
      data-store={store.id}
      href={store.href}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      // MAR-181: carries `platform` on every layout. app_banner_impression has
      // always been segmented by platform, so without this the numerator and
      // the denominator are cut differently and per-platform click-through
      // stops being computable.
      onClick={() => trackEvent(store.clickEvent, { platform })}
      // The stores' clear space, inline so no utility class can reduce it.
      style={{ padding: `${BADGE_CLEAR_SPACE}px` }}
    >
      <img
        src={store.badgeSrc}
        alt={store.badgeAlt}
        width={store.badgeWidth}
        height={BADGE_HEIGHT}
        className="block"
        // Inline so no utility class can ever shrink it below the shared 40px
        // height.
        style={{ height: `${BADGE_HEIGHT}px`, width: "auto" }}
      />
    </a>
  );

  // h-11 w-11 is a 44px touch target, Apple's minimum, on every platform.
  const closeButton = (
    <button
      type="button"
      onClick={handleDismiss}
      aria-label="Close the app download banner"
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-charcoal transition-colors duration-200 hover:bg-cream hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <X className="h-5 w-5" />
    </button>
  );

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Capsule Chic Hub app"
      onKeyDown={handleKeyDown}
      onFocusCapture={(event) => {
        // Remember where focus came from, once, on the way in.
        if (
          focusBeforeBanner.current === null &&
          !event.currentTarget.contains(event.relatedTarget as Node | null)
        ) {
          focusBeforeBanner.current = event.relatedTarget;
        }
      }}
      data-position={inline ? "inline" : "fixed"}
      className={
        inline
          ? "relative border-t border-light-taupe bg-warm-white"
          : "fixed inset-x-0 bottom-0 z-40 border-t border-light-taupe bg-warm-white shadow-lg"
      }
    >
      {platform === "other" ? (
        <>
          {/* pb-[calc(...)] clears the home-indicator strip on iPhone, and the
              gesture bar on Android phones drawing edge to edge. Without it the
              close button and the badge sit under the home bar in landscape, in
              standalone, and whenever Safari's toolbar collapses. Same idiom as
              PublicQuizShell's sticky CTA. Written as a class rather than an
              inline style because jsdom's CSSOM discards `env()` as invalid, which
              would make it silently untestable. */}
          {/* flex-wrap is a layout on narrow desktops. There are two QR codes and
              two badges, about 520px, so the copy keeps a minimum width and the
              stores drop to a second line on a narrow window rather than squeezing
              the copy into a column. Without wrapping, a narrow viewport would push
              the close button off the edge, and an undismissable fixed banner is a
              much worse failure than a taller one. */}
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-6 lg:px-8">
            <div className="min-w-[16rem] flex-1">
              <p className="font-playfair text-base font-semibold text-charcoal">
                Take my picks with you
              </p>
              <p className="mt-0.5 text-sm leading-relaxed text-charcoal">
                {DESKTOP_COPY}
              </p>
            </div>

            {/* The stores travel as one unit, so on a narrow desktop both drop to
                the second line together instead of splitting across two. */}
            <div className="flex shrink-0 items-center gap-4">
              {STORES_FOR_PLATFORM.other.map((id) => {
                const store = STORES[id];
                return (
                  <div
                    key={store.id}
                    data-store={store.id}
                    className="flex shrink-0 items-center gap-2"
                  >
                    {/* Not a link and not a button: the badge beside it is the
                        single CTA for its store, and the badge is what tells the
                        reader which code is for which phone. */}
                    <img
                      src={store.qrSrc}
                      alt={store.qrAlt}
                      width={QR_SIZE}
                      height={QR_SIZE}
                      className="block shrink-0 rounded-sm border border-light-taupe"
                      // Inline so no utility class can shrink it below a scannable
                      // size, and so the box is reserved before Tailwind applies.
                      style={{ width: `${QR_SIZE}px`, height: `${QR_SIZE}px` }}
                    />
                    {badgeLink(store)}
                  </div>
                );
              })}
            </div>

            {closeButton}
          </div>
        </>
      ) : (
        <>
          {/* One row at every phone width: copy, badge, close. At 360px the
              Play badge with its clear space (155px), the 44px close target
              and the side padding leave the copy about 135px, enough for the
              heading and the short line on one line each. min-w-0 lets the
              copy truncate rather than push the close control off the edge.
              No gap either side of the badge link: its padding is the clear
              space and may meet the row's edges, but nothing may sit inside
              it. py-2 because the badge link is 60px tall with its clear
              space, which keeps the row about 76px. pb-[calc(...)] is the
              same safe-area padding the desktop row explains. */}
          <div
            data-layout="phone"
            className="mx-auto flex max-w-7xl items-center py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pl-4 pr-1"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-[15px] font-semibold leading-5 text-charcoal">
                {PHONE_HEADING}
              </p>
              <p className="mt-0.5 truncate text-[13px] leading-4 text-charcoal">
                {PHONE_COPY}
              </p>
            </div>
            {STORES_FOR_PLATFORM[platform].map((id) => badgeLink(STORES[id]))}
            {closeButton}
          </div>
        </>
      )}
    </div>
  );
}
